import { Typography } from '@mui/material';
import React, { Fragment, useContext } from 'react';
import { UserContext } from '../../../auth/UserContext';
import { useI18n } from '../../../i18n/I18n';
import { useEngagementTracker } from './EngagementContext';
import { getSentenceKey } from './engagementUtils';

const linkStyle = {
    color: 'primary.main',
    fontWeight: 500,
    textDecoration: 'none',
    cursor: 'pointer'
};

export interface EngagementInformationProps {
    contextTerm: string | null;
    /** The type of engagement */
    type: string;
    /** The label for the engagement information */
    label: string;
    /** Information about the person who created the engagement */
    createdByPerson: { id: string; name: string };
    /** Optional callback function to be invoked when the type is clicked */
    onTypeClick?: () => void;
    /** Optional shortlist ID for rendering a hyperlink */
    shortlistId?: string;
}

export function EngagementInformation({
    contextTerm,
    type,
    label,
    createdByPerson,
    onTypeClick,
    shortlistId
}: EngagementInformationProps) {
    const t = useI18n();
    const { setClickedPerson, setClickedEngagement } = useEngagementTracker();
    const { application } = useContext(UserContext);

    const handleTypeOrLabelClick = () => {
        onTypeClick?.();
    };

    const handleNameClick = () => {
        setClickedEngagement(null);
        setClickedPerson(createdByPerson.id);
    };

    const hasCreatedBy = Boolean(createdByPerson);
    const key = getSentenceKey(type, contextTerm, label, hasCreatedBy);

    const raw = t(key);
    const parts = raw
        .replace('__TYPE__', '%%TYPE%%')
        .replace('__LABEL__', '%%LABEL%%')
        .replace('__CONTEXT__', '%%CONTEXT%%')
        .replace('__NAME__', '%%NAME%%')
        .split(/(%%TYPE%%|%%LABEL%%|%%CONTEXT%%|%%NAME%%)/);

    return (
        <Typography component="span">
            {parts.map((part, index) => {
                switch (part) {
                    case '%%TYPE%%':
                        return (
                            <Typography
                                key={`part-${index}`}
                                component="span"
                                sx={linkStyle}
                                onClick={handleTypeOrLabelClick}
                            >
                                {type}
                            </Typography>
                        );

                    case '%%LABEL%%':
                        return shortlistId ? (
                            <Typography
                                key={`part-${index}`}
                                component="a"
                                href={`${application.home}/shortlists/${shortlistId}/details${window.location.search}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={linkStyle}
                            >
                                {label}
                            </Typography>
                        ) : (
                            <Typography
                                key={`part-${index}`}
                                component="span"
                                sx={linkStyle}
                                onClick={handleTypeOrLabelClick}
                            >
                                {label}
                            </Typography>
                        );

                    case '%%CONTEXT%%':
                        return (
                            <Typography key={`part-${index}`} component="span">
                                {contextTerm}
                            </Typography>
                        );

                    case '%%NAME%%':
                        return hasCreatedBy ? (
                            <Typography key={`part-${index}`} component="span" sx={linkStyle} onClick={handleNameClick}>
                                {createdByPerson.name}
                            </Typography>
                        ) : null;

                    default:
                        return <Fragment key={`part-${index}`}>{part}</Fragment>;
                }
            })}
        </Typography>
    );
}
